import { useAuth } from "@tellescope/tanstack-query";
import { Combobox, ComboboxOption, Input, Label } from "@tellescope/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useTokenResourceOptions } from "../../data/useTemplateTokenData";
import { ENDUSER_FIELDS, PORTAL_DESTINATIONS } from "../../tokens/constants";
import type { TokenDataSource, TokenParamSpec } from "../../tokens/types";

export interface TokenParamFieldProps {
  param: TokenParamSpec;
  /** Resource list to search for `resourceId` params (from the token definition). */
  dataSource: TokenDataSource | undefined;
  value: string;
  onChange: (value: string) => void;
  /** Suppress the field's own label when the collector heading already names it (avoids duplicates). */
  hideLabel?: boolean;
}

/**
 * Renders the input for a single token param, choosing the widget from `param.kind`: a searchable
 * server-backed combobox for resource ids, a static combobox for portal pages and patient fields,
 * or a plain text input otherwise. This is what lets one generic collector serve every token.
 */
export const TokenParamField = ({ param, dataSource, value, onChange, hideLabel }: TokenParamFieldProps) => {
  const { t } = useTranslation("templates");
  const { selectedOrganization } = useAuth();
  const {
    options: resourceOptions,
    search: resourceSearch,
    setSearch,
  } = useTokenResourceOptions(param.kind === "resourceId" ? dataSource : undefined);

  const enduserFieldOptions = useMemo<ComboboxOption[]>(() => {
    const builtIn = ENDUSER_FIELDS.map((field) => ({ value: field.field, label: field.getLabel() }));
    const customFields = selectedOrganization?.settings?.endusers?.customFields ?? [];
    return [...builtIn, ...customFields.map((customField) => ({ value: customField.field, label: customField.field }))];
  }, [selectedOrganization?.settings?.endusers?.customFields]);

  const portalOptions = useMemo<ComboboxOption[]>(
    () => PORTAL_DESTINATIONS.map((destination) => ({ value: destination.field, label: destination.getLabel() })),
    []
  );

  // Derive the selected option from `value` so the field is fully controlled — this is what makes the
  // edit-in-place pre-fill show up. Fall back to the raw value as its own label when no option matches
  // (e.g. a server-backed resource whose friendly name hasn't loaded yet).
  const selectedFrom = (options: ComboboxOption[]): ComboboxOption | undefined =>
    value ? options.find((option) => option.value === value) ?? { value, label: value } : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      {!hideLabel && (
        <Label className="text-xs text-muted-foreground">
          {param.required ? param.getLabel() : t("{{label}} (optional)", { label: param.getLabel() })}
        </Label>
      )}
      {param.kind === "resourceId" ? (
        <Combobox
          value={selectedFrom(resourceOptions)}
          onChange={(option) => onChange(option?.value ?? "")}
          options={resourceOptions}
          searchQuery={resourceSearch}
          setSearchQuery={setSearch}
          customFilter={(_, opts) => opts}
          placeholder={t("Search and select…")}
          searchPlaceholder={t("Search…")}
        />
      ) : param.kind === "field" ? (
        <Combobox
          value={selectedFrom(enduserFieldOptions)}
          onChange={(option) => onChange(option?.value ?? "")}
          options={enduserFieldOptions}
          placeholder={t("Search and select a field")}
          searchPlaceholder={t("Search…")}
        />
      ) : param.kind === "portalDestination" ? (
        <Combobox
          value={selectedFrom(portalOptions)}
          onChange={(option) => onChange(option?.value ?? "")}
          options={portalOptions}
          placeholder={t("Select a page")}
        />
      ) : (
        <Input
          type="text"
          value={value}
          placeholder={
            param.kind === "url"
              ? "https://"
              : param.kind === "displayText" && !param.required
              ? t("Leave blank to show the link itself")
              : undefined
          }
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
};
